import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebounce } from '@uidotdev/usehooks';

/**
 * Debounces calls to `fetch`: the returned `requestRefetch` can be called any
 * number of times, and `fetch` runs once `delayMs` after the last call, using
 * the latest `fetch` passed in.
 *
 * Requests are counted rather than flagged. A boolean flag that is reset after
 * each fetch can flip false -> true inside the debounce window, so the debounced
 * value never changes and the later request is silently dropped -- leaving the
 * page showing results for an earlier filter/sort/page.
 */
export function useDebouncedRefetch(
  fetch: () => void,
  delayMs: number,
): () => void {
  const [requestCount, setRequestCount] = useState(0);
  const debouncedRequestCount = useDebounce(requestCount, delayMs);

  const fetchRef = useRef(fetch);
  useEffect(() => {
    fetchRef.current = fetch;
  });

  useEffect(() => {
    if (debouncedRequestCount > 0) {
      fetchRef.current();
    }
  }, [debouncedRequestCount]);

  return useCallback(() => setRequestCount((count) => count + 1), []);
}
